<script lang="ts" setup>
import OmniDialog from "@/components/OmniDialog.vue";
import TruckStatus from "@/components/status/TruckStatus.vue";
import TruckLocationInfo from "@/components/truck/TruckLocationInfo.vue";
import TruckMember from "@/components/truck/TruckMember.vue";
import { computed, ref, useTemplateRef, watch } from "vue";
import type { RouteStopDto, TravelOrderDto } from "@/api/trips/dto/trip.dto";
import type { TruckDto } from "@/api/trucks/dto/truck.dto";
import type { VForm } from "vuetify/components";
import OmniSelect from "@/components/controls/OmniSelect.vue";
import { RouteStopStatus, TravelOrderEditStatusMap, TravelOrderStatus, TravelOrderStatusMap } from "@/data/trip";
import { OrderEditStatusMap, OrderStatus, OrderStatusMap } from "@/data/order";
import OrderFile from "@/views/orders/components/edit/OrderFile.vue";
import { API } from "@/api/api";
import { useSnackbarStore } from "@/store/snackbar.store";
import AlertMessage from "@/components/AlertMessage.vue";
import { useValidation } from "@/composables/useValidation";

const { requiredValidator } = useValidation();

const isEditDialog = defineModel<boolean>({ default: false });
const travelOrder = defineModel<TravelOrderDto | null>("travelOrder", { default: null });

const props = defineProps<{
  truck: TruckDto | null;
  travelOrderFromApi: TravelOrderDto | null;
  routeStops: RouteStopDto[];
  travelOrdersByOrder: Record<string, TravelOrderDto[]>;
}>();

const emits = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "update:travelOrder", travelOrder: TravelOrderDto): void;
}>();

const api = new API();
const { showSnackbarError } = useSnackbarStore();

const loading = ref<boolean>(false);
const travelOrderStatusModel = ref<TravelOrderStatus>(travelOrder.value?.status || TravelOrderStatus.inProgress);
const orderStatusModel = ref<OrderStatus>(travelOrder.value?.order.status || OrderStatus.inProgress);
const driverRate = ref<string | number>(travelOrder.value?.rate || "");
const orderRate = ref<string | number>(travelOrder.value?.rate || "");
const tonuFile = ref<File | null>(null);
const isTravelOrderDetail = ref<boolean>(false);
const isOrderDetail = ref<boolean>(false);
const editFormRef = useTemplateRef<VForm | null>("editForm");

const isTravelOrderStatusDisabled = computed(() => {
  const acceptedStatuses = [RouteStopStatus.inRoute, RouteStopStatus.waitingForLoading, RouteStopStatus.notStarted];

  return props.routeStops.some((routeStop) => !acceptedStatuses.includes(routeStop.status));
});

const travelOrderStatusOptions = computed(() => {
  const dryRunStatuses = [TravelOrderStatus.dryRun, TravelOrderStatus.waitingForDryRun, TravelOrderStatus.noDryRun];

  if (props.travelOrderFromApi && dryRunStatuses.includes(props.travelOrderFromApi.status)) {
    return TravelOrderEditStatusMap.filter((status) => status.id !== TravelOrderStatus.cancelled);
  }

  return TravelOrderEditStatusMap.filter((status) => status.id !== TravelOrderStatus.noDryRun);
});

const selectedStatusName = computed(() => {
  return TravelOrderStatusMap.find((status) => status.id === travelOrderStatusModel.value)?.name;
});

const travelOrdersByOrder = computed<TravelOrderDto[]>(() => {
  return props.travelOrdersByOrder[travelOrder.value?.orderId || ""].map((to) => {
    if (to.id === travelOrder.value?.id) {
      return {
        ...to,
        status: travelOrderStatusModel.value,
      };
    }

    return { ...to };
  });
});

const isOrderStatusDisabled = computed(() => {
  const acceptedStatuses = [
    TravelOrderStatus.dryRun,
    TravelOrderStatus.waitingForDryRun,
    TravelOrderStatus.noDryRun,
    TravelOrderStatus.cancelled,
  ];

  return travelOrdersByOrder.value.some((travelOrder) => !acceptedStatuses.includes(travelOrder.status));
});

const orderStatusOptions = computed(() => {
  const tonuStatuses = [OrderStatus.tonu, OrderStatus.noTonu, OrderStatus.waitingTonu];

  if (props.travelOrderFromApi?.order.status && tonuStatuses.includes(props.travelOrderFromApi.order.status)) {
    return OrderEditStatusMap.filter((status) => status.id !== OrderStatus.cancelled);
  }

  return OrderEditStatusMap.filter((status) => status.id !== OrderStatus.noTonu);
});

const selectedOrderStatusName = computed(() => {
  return OrderStatusMap.find((status) => status.id === orderStatusModel.value)?.name || "";
});

const isUndoProcess = computed(() => {
  return (
    travelOrderStatusModel.value === TravelOrderStatus.inProgress &&
    travelOrder.value?.status !== TravelOrderStatus.inProgress
  );
});

const fileLabel = computed(() => {
  if (isUndoProcess.value) {
    return "Rate confirmation";
  }

  return "TONU file";
});

async function editTravelOrder() {
  if (!editFormRef.value || !travelOrder.value) {
    return;
  }

  const result = await editFormRef.value.validate();

  if (!result.valid) {
    return;
  }

  loading.value = true;

  if (isOrderDetail.value && tonuFile.value) {
    const fileResponse = await api.files.uploadFile("orders", tonuFile.value, [
      `order:${travelOrder.value.orderId}`,
      OrderStatus.tonu,
    ]);

    if (!fileResponse.success) {
      showSnackbarError("Error uploading TONU file");
      loading.value = false;
      return;
    }
  }

  const updatedTravelOrder: TravelOrderDto = {
    ...travelOrder.value,
    status: travelOrderStatusModel.value,
    rate: Number(driverRate.value),
    order: {
      ...travelOrder.value.order,
      status: orderStatusModel.value,
      rate: Number(orderRate.value),
    },
  };

  loading.value = false;

  emits("update:modelValue", false);
  emits("update:travelOrder", updatedTravelOrder);
}

watch(isOrderStatusDisabled, (value, prevValue) => {
  if (!value && prevValue) {
    orderStatusModel.value = OrderStatus.unassigned;
  }
});

watch(
  travelOrderStatusModel,
  (value) => {
    if (value === TravelOrderStatus.dryRun) {
      isTravelOrderDetail.value = true;
    } else {
      isTravelOrderDetail.value = false;
      driverRate.value = "";
    }
  },
  {
    immediate: true,
  },
);

watch(
  orderStatusModel,
  (value) => {
    if (value === OrderStatus.tonu) {
      isOrderDetail.value = true;
    } else if (value === OrderStatus.noTonu || value === OrderStatus.cancelled) {
      isOrderDetail.value = false;
      orderRate.value = "";
    } else {
      isOrderDetail.value = false;
      orderRate.value = "";
    }
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <omni-dialog
    v-if="travelOrder"
    v-model="isEditDialog"
    :loading="loading"
    primary-action-label="Save"
    title="Edit travel order"
    @primary-action:click="editTravelOrder"
  >
    <template #title:append>
      <span class="text-primary ml-2 line-height-24px">#{{ travelOrder.id }}</span>
    </template>
    <v-form ref="editForm" class="py-4">
      <div v-if="truck" class="pa-4 mb-5 bg-grey-lighten-4 rounded">
        <div class="d-flex align-center">
          <router-link
            :to="{ name: 'truck-view', params: { id: truck.id } }"
            class="text-primary text-decoration-none font-weight-500"
            data-qa="edit-travel-order-truck-number"
            target="_blank"
          >
            Truck {{ truck.number }}
          </router-link>

          <truck-status :truck="truck" class="ml-3" />
        </div>

        <div class="mt-2">
          <span class="font-size-11 text-grey-darken-1 font-weight-600">Current location</span>

          <truck-location-info :location="truck.currentLocation" :time="truck.currentLocationTime" />
        </div>

        <div class="mt-4">
          <truck-member
            v-if="truck.driver"
            :member="truck.driver"
            class="d-flex align-center"
            hide-status
            label-class="bg-shades-white"
            language-light
          />

          <truck-member
            :member="truck.owner"
            class="d-flex align-center mt-3"
            hide-status
            label-class="bg-shades-white text-green-lighten-1"
            language-light
          />
        </div>
      </div>

      <p class="text-grey-darken-1">Travel order information</p>

      <div class="d-flex align-center justify-space-between col-gap-4 mt-4">
        <omni-select
          v-model="travelOrderStatusModel"
          :disabled="isTravelOrderStatusDisabled"
          :items="travelOrderStatusOptions"
          :rules="[requiredValidator]"
          data-qa="edit-travel-order-status-field"
          label="Travel order status"
          required
        >
          <template #selection> {{ selectedStatusName }}</template>
        </omni-select>

        <v-text-field
          v-if="isTravelOrderDetail"
          v-model="driverRate"
          :rules="[requiredValidator]"
          class="required"
          color="primary"
          data-qa="edit-travel-order-driver-rate-field"
          density="compact"
          hide-details="auto"
          label="Driver rate"
          max-width="150"
          type="number"
          variant="outlined"
        >
          <template #append-inner>
            <span class="font-size-16 text-grey-darken-1">$</span>
          </template>
        </v-text-field>
      </div>

      <alert-message
        v-if="isTravelOrderStatusDisabled"
        bg-color="indigo-lighten-5"
        border-color="none"
        class="mt-4 line-height-16px"
        icon-color="indigo"
      >
        <span class="text-indigo">
          Travel order status can’t be changed, truck has already been loaded at the route stop
        </span>
      </alert-message>

      <div class="mt-5">
        <div class="d-flex align-center col-gap-1">
          <router-link
            :to="{ name: 'order-view', params: { id: travelOrder.orderId } }"
            class="text-decoration-none text-primary"
            target="_blank"
          >
            Order#{{ travelOrder.order.number }}
          </router-link>
          <span class="text-grey-darken-1"> information:</span>
        </div>

        <div class="d-flex align-center justify-space-between mt-4 col-gap-4">
          <omni-select
            v-model="orderStatusModel"
            :disabled="isOrderStatusDisabled"
            :items="orderStatusOptions"
            :rules="[requiredValidator]"
            data-qa="order-status-select"
            label="Order status"
            required
          >
            <template #selection>
              {{ selectedOrderStatusName }}
            </template>
          </omni-select>

          <v-text-field
            v-if="isOrderDetail"
            v-model="orderRate"
            :rules="[requiredValidator]"
            class="required"
            color="primary"
            density="compact"
            hide-details="auto"
            label="Order rate"
            max-width="150"
            type="number"
            variant="outlined"
            data-qa="edit-travel-order-order-rate-field"
          >
            <template #append-inner>
              <span class="font-size-16 text-grey-darken-1">$</span>
            </template>
          </v-text-field>
        </div>

        <order-file v-if="isOrderDetail" v-model="tonuFile" :label="fileLabel" class="mt-4"></order-file>
      </div>

      <alert-message
        v-if="isOrderStatusDisabled"
        bg-color="indigo-lighten-5"
        border-color="none"
        class="mt-4 line-height-16px"
        icon-color="indigo"
      >
        <span class="text-indigo"> Order status can’t be changed while it has active travel orders </span>
      </alert-message>
    </v-form>
  </omni-dialog>
</template>

<style lang="scss" scoped>
.travel-number {
  position: relative;
  right: 18px;
}
</style>
